import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Mail, Lock, Shield, AlertCircle, Loader2, Key, QrCode, CheckCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLogin = () => {
  const navigate = useNavigate();
  const { setUser, setToken } = useAuth();
  
  // Login flow states
  const [step, setStep] = useState('email'); // 'email', 'totp', 'setup'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [tempToken, setTempToken] = useState('');
  
  // 2FA Setup states
  const [qrCode, setQrCode] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  
  // UI states
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Safe API call that avoids Response body issues
  const safeApiCall = async (endpoint, body) => {
    try {
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      
      // Read body as text first (can only read once)
      const responseText = await response.text();
      
      // Try to parse as JSON
      let responseData;
      try {
        responseData = JSON.parse(responseText);
      } catch (parseError) {
        responseData = { detail: responseText };
      }
      
      return { 
        ok: response.ok, 
        status: response.status,
        data: responseData 
      };
    } catch (networkError) {
      return { 
        ok: false, 
        status: 0,
        data: { detail: 'Network error. Please check your connection.' } 
      };
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Step 1: Try to login
      const loginResult = await safeApiCall('/api/admin-auth/login', { email, password });
      
      // If requires 2FA setup
      if (loginResult.data.requires_setup) {
        // Step 2: Get QR code for setup
        const setupResult = await safeApiCall('/api/admin-auth/setup-2fa', { email, password });
        
        if (!setupResult.ok) {
          throw new Error(setupResult.data.detail || 'Failed to setup 2FA');
        }
        
        setQrCode(setupResult.data.qr_code);
        setSecretKey(setupResult.data.secret);
        setTempToken(setupResult.data.temp_token);
        setStep('setup');
        return;
      }
      
      if (!loginResult.ok) {
        throw new Error(loginResult.data.detail || 'Login failed');
      }

      // Password verified, need TOTP
      setTempToken(loginResult.data.temp_token);
      setStep('totp');
    } catch (err) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleTOTPSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await safeApiCall('/api/admin-auth/verify-totp', {
        email,
        totp_code: totpCode,
        temp_token: tempToken
      });

      if (!result.ok) {
        throw new Error(result.data.detail || 'Invalid 2FA code');
      }

      // Login successful
      localStorage.setItem('token', result.data.token);
      setToken(result.data.token);
      setUser(result.data.user);
      navigate('/admin');
    } catch (err) {
      setError(err.message || 'Verification failed');
      setTotpCode('');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifySetup = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await safeApiCall('/api/admin-auth/verify-setup', {
        email,
        totp_code: totpCode,
        temp_token: tempToken
      });

      if (!result.ok) {
        throw new Error(result.data.detail || 'Invalid code. Please try again.');
      }

      // Setup complete, login successful
      localStorage.setItem('token', result.data.token);
      setToken(result.data.token);
      setUser(result.data.user);
      navigate('/admin');
    } catch (err) {
      setError(err.message || 'Setup failed');
      setTotpCode('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4 overflow-y-auto">
      <div className="w-full max-w-md my-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-green-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Admin Portal</h1>
          <p className="text-slate-400">Secure administrator access</p>
        </div>

        <Card className="p-6 sm:p-8 bg-slate-800/50 border-slate-700 backdrop-blur-sm">
          {/* Step 1: Email & Password */}
          {step === 'email' && (
            <form onSubmit={handleEmailSubmit} className="space-y-5">
              <div className="text-center mb-6">
                <h2 className="text-lg font-semibold text-white">Sign In</h2>
                <p className="text-sm text-slate-400 mt-1">Enter your admin credentials</p>
              </div>

              {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-400">{error}</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@example.com"
                    required
                    data-testid="admin-email-input"
                    className="w-full pl-10 pr-4 py-3 bg-slate-900/50 border border-slate-600 rounded-xl text-white placeholder-slate-500 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                    data-testid="admin-password-input"
                    className="w-full pl-10 pr-4 py-3 bg-slate-900/50 border border-slate-600 rounded-xl text-white placeholder-slate-500 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                data-testid="admin-login-submit"
                className="w-full py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-xl"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Verifying...
                  </span>
                ) : (
                  'Continue'
                )}
              </Button>

              <p className="text-center text-sm text-slate-500 mt-4">
                Not an admin?{' '}
                <a href="/login" className="text-green-400 hover:text-green-300">
                  User Login
                </a>
              </p>
            </form>
          )}

          {/* Step 2: TOTP Verification */}
          {step === 'totp' && (
            <form onSubmit={handleTOTPSubmit} className="space-y-5">
              <div className="text-center mb-6">
                <div className="w-12 h-12 bg-green-600/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Key className="w-6 h-6 text-green-400" />
                </div>
                <h2 className="text-lg font-semibold text-white">Two-Factor Authentication</h2>
                <p className="text-sm text-slate-400 mt-1">Enter the code from your authenticator app</p>
              </div>

              {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-400">{error}</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">6-Digit Code</label>
                <input
                  type="text"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  required
                  maxLength={6}
                  autoFocus
                  data-testid="admin-totp-input"
                  className="w-full px-4 py-4 text-center text-2xl tracking-[0.5em] font-mono bg-slate-900/50 border border-slate-600 rounded-xl text-white placeholder-slate-600 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              <Button
                type="submit"
                disabled={loading || totpCode.length !== 6}
                data-testid="admin-totp-submit"
                className="w-full py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-xl disabled:opacity-50"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Verifying...
                  </span>
                ) : (
                  'Verify & Login'
                )}
              </Button>

              <button
                type="button"
                onClick={() => { setStep('email'); setTotpCode(''); setError(''); }}
                className="w-full text-sm text-slate-400 hover:text-slate-300"
              >
                Back to login
              </button>
            </form>
          )}

          {/* Step: 2FA Setup */}
          {step === 'setup' && (
            <div className="space-y-4">
              <div className="text-center mb-4">
                <div className="w-10 h-10 bg-amber-600/20 rounded-full flex items-center justify-center mx-auto mb-2">
                  <QrCode className="w-5 h-5 text-amber-400" />
                </div>
                <h2 className="text-lg font-semibold text-white">Set Up 2FA</h2>
                <p className="text-xs text-slate-400 mt-1">Scan QR code with authenticator app</p>
              </div>

              {error && (
                <div className="p-2 bg-red-500/10 border border-red-500/30 rounded-lg flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-400">{error}</p>
                </div>
              )}

              {/* QR Code */}
              {qrCode && (
                <div className="flex justify-center p-3 bg-white rounded-xl">
                  <img src={qrCode.startsWith('data:') ? qrCode : `data:image/png;base64,${qrCode}`} alt="2FA QR Code" className="w-36 h-36" />
                </div>
              )}

              {/* Manual Entry Option */}
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => setShowSecret(!showSecret)}
                  className="text-xs text-slate-400 hover:text-slate-300"
                >
                  {showSecret ? 'Hide' : 'Show'} manual entry key
                </button>
                {showSecret && (
                  <div className="mt-2 p-2 bg-slate-900/50 rounded-lg">
                    <p className="text-xs text-slate-500 mb-1">Secret Key:</p>
                    <p className="font-mono text-xs text-green-400 break-all select-all">{secretKey}</p>
                  </div>
                )}
              </div>

              <div className="p-2 bg-slate-900/50 rounded-lg">
                <p className="text-xs text-slate-400 text-center">
                  Use <span className="text-white">Google Authenticator</span> or <span className="text-white">Authy</span>
                </p>
              </div>

              <form onSubmit={handleVerifySetup} className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">
                    Enter 6-digit code to verify
                  </label>
                  <input
                    type="text"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    required
                    maxLength={6}
                    data-testid="admin-setup-totp-input"
                    className="w-full px-4 py-3 text-center text-xl tracking-[0.5em] font-mono bg-slate-900/50 border border-slate-600 rounded-xl text-white placeholder-slate-600 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading || totpCode.length !== 6}
                  data-testid="admin-setup-submit"
                  className="w-full py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-xl disabled:opacity-50"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Verifying...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      Complete Setup
                    </span>
                  )}
                </Button>
              </form>
            </div>
          )}
        </Card>

        {/* Footer */}
        <p className="text-center text-xs text-slate-500 mt-6">
          Protected by Two-Factor Authentication
        </p>
      </div>
    </div>
  );
};

export default AdminLogin;
